import React, { useCallback, useEffect, useRef, useState } from 'react';
import { View, Text, StyleSheet, ScrollView, TouchableOpacity } from 'react-native';
import { Send } from 'lucide-react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useTheme, SPACING, TYPOGRAPHY, CONTENT_MAX_WIDTH } from '../../theme';
import { Button, Input } from '../common';
import { PanelStack, usePanelStack } from '../layout/PanelStack';
import { ImagePickerSection } from './ImagePickerSection';
import { ObservationTypeSelector } from './ObservationTypeSelector';
import { OptionsPanel } from './OptionsPanel';
import { StructurePickerSection } from './StructurePickerSection';
import { LinkedStructureCard } from './LinkedStructureCard';
import {
  PostLocationSection,
  type PostLocation,
  type PostLocationSectionHandle,
} from './PostLocationSection';
import {
  ChecklistItemPickerSection,
  type SelectedChecklistItem,
} from './ChecklistItemPickerSection';
import { BeforePostSection } from './BeforePostSection';
import { BeforePhotoSection } from './BeforePhotoSection';
import { useAuthStore } from '../../store/authStore';
import { usePostsStore } from '../../store/postsStore';
import { useUIStore } from '../../store/uiStore';
import { useStructuresStore } from '../../store/structuresStore';
import {
  Structure,
  Post,
  normalizePostCategory,
  type PostCategory,
  type PostVisibility,
} from '../../types';
import { useImagePicker } from '@hooks/useImagePicker';
import { usePostSubmit, type PostSubmitOutcome } from '@hooks/usePostSubmit';
import { getComposerDefaults } from '@utils/composerDefaults';

type ObservationType = PostCategory;

const VALID_CATEGORY_PARAMS: ObservationType[] = ['progress', 'risk', 'news'];

export interface PostComposerProps {
  editPostId?: string;
  structureId?: string;
  checklistItemId?: string;
  beforePostId?: string;
  category?: string;
  /** Called once the post is saved/queued/updated — the route turns this
   *  into router.back()/router.replace(), the desktop dock just closes.
   *  See src/hooks/usePostSubmit.ts's PostSubmitOutcome. */
  onDone: (outcome: PostSubmitOutcome) => void;
  /** Set (even to null) only by the desktop docked composer
   *  (ComposerDockPanel.web.tsx, panel-system.md Phase 3A): the draggable
   *  map pin's current position, read here instead of rendering
   *  PostLocationSection's own picker. Undefined (the default, every phone/
   *  route caller) keeps the normal GPS/manual-pick location flow
   *  untouched. */
  dockedMapPin?: { latitude: number; longitude: number } | null;
  /** Reports whether a picker (location/checklist/before-post) is pushed on
   *  top of the form, so a page-level header can hide its own back arrow
   *  while the drill-in's own back arrow is showing (panel-system-review-
   *  notes.md #12a — two back arrows stacked otherwise). Only app/post/
   *  create.tsx's route wrapper needs this; the docked composer
   *  (ComposerDockPanel.web.tsx) has no page header to hide. */
  onDrillInChange?: (isDrilledIn: boolean) => void;
}

/** Reports the host PanelStack's depth to `onDrillInChange` — must render
 *  inside PanelStack's `root` (usePanelStack() throws otherwise). Renders
 *  nothing itself. */
function DrillInReporter({ onChange }: { onChange?: (isDrilledIn: boolean) => void }) {
  const { depth } = usePanelStack();
  useEffect(() => {
    onChange?.(depth > 0);
  }, [depth, onChange]);
  return null;
}

/**
 * The observation composer's body: photo, category, caption, and the
 * link/options section — everything app/post/create.tsx used to render
 * directly. Split out (panel-system.md Phase 3) so the desktop map can dock
 * the same form beside the map (ComposerDockPanel.web.tsx) instead of always
 * routing to /post/create; the route still renders this for phone and deep
 * links. Hosts its own PanelStack so the location/checklist/before-post
 * pickers open as drill-ins instead of AdaptiveSheet dialogs, on every
 * platform.
 */
export function PostComposer({
  editPostId,
  structureId: paramStructureId,
  checklistItemId: paramChecklistItemId,
  beforePostId: paramBeforePostId,
  category: paramCategory,
  onDone,
  dockedMapPin,
  onDrillInChange,
}: PostComposerProps) {
  // /post/create?category=news preselects News (e.g. from the map's news
  // card). Junk values are ignored — the composer falls back to the usual
  // default. Only meaningful for a fresh (non-edit) composer.
  const initialCategoryFromParam: ObservationType | null = VALID_CATEGORY_PARAMS.includes(
    paramCategory as ObservationType
  )
    ? (paramCategory as ObservationType)
    : null;
  const { colors } = useTheme();
  const insets = useSafeAreaInsets();
  const { user } = useAuthStore();
  const { fetchPost, getPostById } = usePostsStore();
  const storeStructure = useStructuresStore((s) => s.selectedStructure);

  const isEditing = !!editPostId;

  const [imageUri, setImageUri] = useState<string | null>(null);
  const [caption, setCaption] = useState('');
  // News-only optional headline (title on the stored doc).
  const [newsTitle, setNewsTitle] = useState('');
  const [observationType, setObservationType] = useState<ObservationType>(
    initialCategoryFromParam ?? 'progress'
  );
  // News is text-first (client ask, Aug 2026): photo optional, no
  // structure/checklist/location/pair sections, and a doc-only write when
  // no photo is attached. Everything keys off the live category so
  // switching the type selector mid-compose reshapes the form.
  const isNews = observationType === 'news';
  const screenTitle = isEditing
    ? isNews
      ? 'Edit News'
      : 'Edit Observation'
    : isNews
      ? 'Share News'
      : 'New Observation';
  // Baseline for the edit flow's location diff at save time — updatePost
  // only needs a `location` in the payload when it actually changed.
  const editInitialLocationRef = useRef<PostLocation | null>(null);

  useEffect(() => {
    if (!editPostId) return;
    fetchPost(editPostId).then((post) => {
      if (!post) return;
      setCaption(post.caption);
      setNewsTitle(post.title ?? '');
      setObservationType(normalizePostCategory(post.category));
      setImageUri(post.imageUrl ?? null);
      setIsAnonymous(post.isAnonymous ?? false);
      setVisibility(post.visibility ?? 'public');
      setEditStructureId(post.structureId);
      setChecklistItem(
        post.checklistItemId ? { id: post.checklistItemId, label: post.checklistItemLabel } : null
      );
      const location = post.location
        ? {
            latitude: post.location.latitude,
            longitude: post.location.longitude,
            address: post.location.address,
          }
        : null;
      setUserLocation(location);
      editInitialLocationRef.current = location;
    });
  }, [editPostId, fetchPost]);

  const [isAnonymous, setIsAnonymous] = useState(false);
  const [visibility, setVisibility] = useState<PostVisibility>('public');
  const [userLocation, setUserLocation] = useState<PostLocation | null>(null);
  const locationSectionRef = useRef<PostLocationSectionHandle>(null);
  const [pickedStructure, setPickedStructure] = useState<Structure | null>(null);
  // The step this observation documents. Seeded from the route param (label
  // resolved by the picker once the checklist loads) or, in edit mode, from
  // the post being edited.
  const [checklistItem, setChecklistItem] = useState<SelectedChecklistItem | null>(
    paramChecklistItemId ? { id: paramChecklistItemId } : null
  );
  // Edit mode has no structure param; the post's own structureId drives the
  // checklist picker.
  const [editStructureId, setEditStructureId] = useState<string | undefined>(undefined);
  // The earlier "before" post this observation pairs with. Create-only —
  // editing an existing pair would require re-snapshotting the denormalized
  // preview fields, deferred for MVP.
  const [beforePost, setBeforePost] = useState<Post | null>(null);
  // Self-contained before/after: a standalone "before" image URI attached
  // directly to this post (no prior post required). Mutually exclusive with
  // the structure-linked beforePost above.
  const [beforeImageUri, setBeforeImageUri] = useState<string | null>(null);

  // Arriving via "Post an after photo" on a post's detail screen.
  useEffect(() => {
    if (!paramBeforePostId || isEditing) return;
    let stale = false;
    getPostById(paramBeforePostId)
      .then((post) => {
        if (!stale && post) setBeforePost(post);
      })
      .catch(() => {
        // Unreadable before-post (visibility rules) — the pair row just
        // doesn't prefill.
      });
    return () => {
      stale = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- store action is stable
  }, [paramBeforePostId, isEditing]);

  // Capture the store's selected structure on mount so it survives the map
  // clearing selectedStructure when the bottom sheet closes during navigation.
  const [initialStructure, setInitialStructure] = useState(() =>
    paramStructureId ? storeStructure : null
  );

  // Navigation from post detail or the checklist screen can land here with a
  // structureId param but an empty map store — fetch by id so the linked
  // structure card still renders.
  const fetchStructure = useStructuresStore((s) => s.fetchStructure);
  useEffect(() => {
    if (!paramStructureId || initialStructure) return;
    let stale = false;
    fetchStructure(paramStructureId).then((s) => {
      if (!stale && s) setInitialStructure(s);
    });
    return () => {
      stale = true;
    };
  }, [paramStructureId, initialStructure, fetchStructure]);

  const linkedStructure = initialStructure ?? pickedStructure;
  const linkedStructureId = paramStructureId || pickedStructure?.id;

  // Docked composer only (dockedMapPin !== undefined): there's no
  // PostLocationSection here to own userLocation, so mirror its own
  // priority rule (a linked structure's centroid always wins over a manual
  // pick) directly against the map pin instead (panel-system.md Phase 3A).
  useEffect(() => {
    if (dockedMapPin === undefined) return;
    if (linkedStructure) {
      setUserLocation({
        latitude: linkedStructure.centroid.latitude,
        longitude: linkedStructure.centroid.longitude,
        address: linkedStructure.address || undefined,
      });
    } else if (dockedMapPin) {
      setUserLocation({ latitude: dockedMapPin.latitude, longitude: dockedMapPin.longitude });
    }
  }, [dockedMapPin, linkedStructure]);

  // Fire Scouts are out looking for hazards, and Glenn asked for the composer
  // to meet them where they are: Risk, posted without their name. Everyone
  // else keeps Progress + attributed. Visibility is deliberately untouched —
  // the ask was anonymity, not privacy, and the claimed-structure default
  // below still needs to win.
  //
  // Seeded in an effect rather than the useState initializers because `user`
  // is null for the first render or two while auth hydrates, and an
  // initializer would silently capture the wrong default. The touched refs
  // are the same guard the visibility seeding uses: a late `user` must never
  // stomp a choice already made by hand.
  // Pre-touched when a valid `category` param already picked a value (e.g.
  // ?category=news) — otherwise the defaults effect below would immediately
  // overwrite it with defaults.category on mount.
  const categoryTouchedRef = useRef(initialCategoryFromParam !== null);
  const anonTouchedRef = useRef(false);
  const defaults = getComposerDefaults(user?.roles);
  useEffect(() => {
    if (isEditing) return;
    if (!categoryTouchedRef.current) setObservationType(defaults.category);
    if (!anonTouchedRef.current) setIsAnonymous(defaults.isAnonymous);
  }, [isEditing, defaults.category, defaults.isAnonymous]);

  // When the claimer posts on their own property, start the visibility
  // picker from the claim's defaultPostVisibility (owner-set on the
  // structure screen). Each post can still override; other authors and
  // edits are untouched. Skipped once the user touches the picker so a
  // slow claim fetch can't stomp an explicit choice.
  const getClaimForStructure = useStructuresStore((s) => s.getClaimForStructure);
  const visibilityTouchedRef = useRef(false);
  useEffect(() => {
    if (isEditing || !linkedStructureId || !user) return;
    let stale = false;
    getClaimForStructure(linkedStructureId)
      .then((claim) => {
        if (stale || visibilityTouchedRef.current) return;
        if (claim?.claimedBy === user.id && claim.defaultPostVisibility) {
          setVisibility(claim.defaultPostVisibility);
        }
      })
      .catch(() => {
        // Claim unreadable — keep the public default.
      });
    return () => {
      stale = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- store action is stable
  }, [isEditing, linkedStructureId, user]);

  const { showPicker } = useImagePicker();
  const showImagePicker = useCallback(() => showPicker(setImageUri), [showPicker]);

  const { isSubmitting, handleSubmit } = usePostSubmit({
    isEditing,
    editPostId,
    caption,
    newsTitle,
    observationType,
    checklistItem,
    visibility,
    isAnonymous,
    userLocation,
    editInitialLocationRef,
    imageUri,
    linkedStructureId,
    beforePost,
    beforeImageUri,
    locationSectionRef,
    onDone,
  });

  // The submit button is intentionally always enabled (never `disabled`) —
  // RN-Web doesn't reliably block clicks on a disabled TouchableOpacity,
  // and a button that looks dead but still fires is worse UX than one that
  // looks live and validates inline.

  return (
    <PanelStack
      columnMaxWidth={CONTENT_MAX_WIDTH}
      root={
        <>
          <DrillInReporter onChange={onDrillInChange} />
          <ScrollView
            style={styles.scrollView}
            contentContainerStyle={[
              styles.scrollContent,
              { paddingBottom: insets.bottom + SPACING.xl },
            ]}
            keyboardShouldPersistTaps="handled">
            <View style={styles.contentConstraint}>
              <Text style={[styles.screenTitle, { color: colors.text }]}>{screenTitle}</Text>

              {/* Primary cluster: the observation itself — photo, what it is,
                and the words. A scout standing in front of something captures
                this first; everything else is linking and options. */}
              {!isEditing && (
                <ImagePickerSection imageUri={imageUri} onPickImage={showImagePicker} />
              )}

              {/* Self-contained before/after: shown only when there is no
                structure-linked beforePost (the two mechanisms are mutually
                exclusive — decision 4). Hidden during editing. */}
              {!isEditing && !beforePost && !isNews && (
                <BeforePhotoSection
                  imageUri={imageUri}
                  value={beforeImageUri}
                  onChange={(uri) => {
                    setBeforeImageUri(uri);
                    // Clearing the standalone before should not affect beforePost
                    // (it's already null when this section is visible).
                  }}
                />
              )}

              <ObservationTypeSelector
                observationType={observationType}
                onTypeChange={(t) => {
                  categoryTouchedRef.current = true;
                  setObservationType(t);
                }}
              />

              {/* Informational, not gated: opens with no stashed composer path,
                so ObservationGuidanceSheet's Continue just closes it. News
                doesn't need "what to look for in the field" guidance. */}
              {!isNews && (
                <TouchableOpacity
                  onPress={() => {
                    useUIStore.getState().setObservationGuidanceNext(null);
                    useUIStore.getState().openSheet('observationGuidance');
                  }}
                  accessibilityRole="link"
                  accessibilityLabel="What should I look for?"
                  style={styles.guidanceLink}
                  testID="composer-guidance-link">
                  <Text style={[styles.guidanceLinkText, { color: colors.primary }]}>
                    What should I look for?
                  </Text>
                </TouchableOpacity>
              )}

              {/* Optional news headline. Without one the News tab derives a lede
                from the caption's first line (splitNewsCaption). */}
              {isNews && (
                <Input
                  label="Headline (optional)"
                  placeholder="One short line, e.g. Chipping day moved to Friday"
                  value={newsTitle}
                  onChangeText={setNewsTitle}
                  maxLength={100}
                  containerStyle={styles.titleInput}
                  testID="news-title-input"
                />
              )}

              <Input
                label={isNews ? 'News' : 'Caption'}
                placeholder={
                  isNews
                    ? 'What is happening in wildfire preparation near you?'
                    : 'Describe your observation...'
                }
                value={caption}
                onChangeText={setCaption}
                multiline
                numberOfLines={4}
                containerStyle={styles.captionInput}
              />

              {isEditing ? (
                <>
                  {/* Structure-linked posts render no pin of their own (the
                    structure pin represents them) — moving the coordinate
                    would be moot, so the edit affordance is unlinked-only.
                    News is open-ended: no location or checklist affordance. */}
                  {!editStructureId && !isNews && (
                    <PostLocationSection
                      linkedStructure={null}
                      hasStructureParam={false}
                      autoLocate={false}
                      userLocation={userLocation}
                      onChangeLocation={setUserLocation}
                    />
                  )}
                  {!isNews && (
                    <ChecklistItemPickerSection
                      structureId={editStructureId}
                      value={checklistItem}
                      onChange={setChecklistItem}
                    />
                  )}

                  <OptionsPanel
                    isAnonymous={isAnonymous}
                    visibility={visibility}
                    onToggleAnonymous={() => setIsAnonymous(!isAnonymous)}
                    onVisibilityChange={setVisibility}
                  />
                </>
              ) : (
                <>
                  <Text style={[styles.sectionDivider, { color: colors.textSecondary }]}>
                    {isNews ? 'Options' : 'Link & options'}
                  </Text>

                  {!isNews && !paramStructureId && !pickedStructure && (
                    <StructurePickerSection
                      onPick={(s) => {
                        setPickedStructure(s);
                        // A different structure means a different checklist and
                        // different pairable posts — don't carry stale links across.
                        setChecklistItem(null);
                        setBeforePost(null);
                      }}
                    />
                  )}

                  {!isNews && linkedStructure && (
                    <LinkedStructureCard
                      structure={linkedStructure}
                      onClear={
                        paramStructureId
                          ? undefined
                          : () => {
                              setPickedStructure(null);
                              setChecklistItem(null);
                              setBeforePost(null);
                            }
                      }
                    />
                  )}

                  {!isNews &&
                    (dockedMapPin !== undefined ? (
                      // Desktop dock only: location follows the draggable map
                      // pin (ComposerDockPanel.web.tsx) instead of
                      // PostLocationSection's own GPS/manual-pick flow.
                      <View style={styles.section}>
                        <Text style={[styles.locationHint, { color: colors.textSecondary }]}>
                          {linkedStructure
                            ? 'Location set by linked property'
                            : 'Drag the pin on the map to set the location'}
                        </Text>
                      </View>
                    ) : (
                      <PostLocationSection
                        ref={locationSectionRef}
                        linkedStructure={linkedStructure}
                        hasStructureParam={!!paramStructureId}
                        userLocation={userLocation}
                        onChangeLocation={setUserLocation}
                      />
                    ))}

                  {!isNews && (
                    <ChecklistItemPickerSection
                      structureId={linkedStructureId}
                      value={checklistItem}
                      onChange={setChecklistItem}
                    />
                  )}

                  {/* Hidden when the standalone BeforePhotoSection has a value
                    (the two mechanisms are mutually exclusive). */}
                  {!isNews && !beforeImageUri && (
                    <BeforePostSection
                      structureId={linkedStructureId}
                      value={beforePost}
                      onChange={setBeforePost}
                    />
                  )}

                  <OptionsPanel
                    isAnonymous={isAnonymous}
                    visibility={visibility}
                    onToggleAnonymous={() => {
                      anonTouchedRef.current = true;
                      setIsAnonymous(!isAnonymous);
                    }}
                    onVisibilityChange={(v) => {
                      visibilityTouchedRef.current = true;
                      setVisibility(v);
                    }}
                  />
                </>
              )}

              {/* Ink pill (mockup component board). The category is carried as
                information by the dot-label under the type selector, not by the
                colour of the submit button. */}
              <Button
                title={isEditing ? 'Save Changes' : isNews ? 'Post News' : 'Post Observation'}
                onPress={handleSubmit}
                size="lg"
                fullWidth
                leftIcon={Send}
                loading={isSubmitting}
                style={styles.submitButton}
              />
            </View>
          </ScrollView>
        </>
      }
    />
  );
}

const styles = StyleSheet.create({
  scrollView: { flex: 1 },
  scrollContent: { alignItems: 'center' },
  contentConstraint: {
    width: '100%',
    maxWidth: CONTENT_MAX_WIDTH,
    padding: SPACING.md,
  },
  screenTitle: {
    ...TYPOGRAPHY.screenTitle,
    marginBottom: SPACING.md,
  },
  captionInput: { marginBottom: SPACING.md },
  titleInput: { marginBottom: SPACING.md },
  guidanceLink: {
    marginTop: -SPACING.xs,
    marginBottom: SPACING.md,
  },
  guidanceLinkText: {
    ...TYPOGRAPHY.label,
  },
  sectionDivider: {
    ...TYPOGRAPHY.overline,
    marginTop: SPACING.sm,
    marginBottom: SPACING.smd,
  },
  section: {
    marginBottom: SPACING.md,
  },
  locationHint: {
    ...TYPOGRAPHY.bodySmall,
  },
  submitButton: { marginTop: SPACING.sm },
});
